import { campaignCouponService } from "@/api/campaignCouponService";
import { campaignService } from "@/api/campaignService";
import { packageCouponService } from "@/api/packageCouponService";
import { packageService } from "@/api/packageService";
import type {
  CampaignCouponWithId,
  CouponList,
  CouponListItem,
  CouponWithId,
} from "@/resources/coupon";
import type { LearnerResponse } from "@/resources/learner";
import { getLearnersByIds } from "./learnersUtils";

export async function getCouponList(
  collectionName: "packages" | "campaigns",
  docId: string,
): Promise<CouponList> {
  const couponList: CouponList = {
    code: "",
    name: "",
    totalCouponCount: 0,
    usedCouponCount: 0,
    unusedCouponCount: 0,
    coupons: [],
  };

  const couponInfo = await getCouponInfo(collectionName, docId);
  if (!couponInfo) return couponList;

  couponList.code = couponInfo.code;
  couponList.name = couponInfo.name;
  couponList.totalCouponCount = couponInfo.totalCouponCount;

  const coupons = await getCoupons(collectionName, docId);
  if (!coupons) return couponList;

  const couponStats = await getCouponStats(coupons);
  if (!couponStats) return couponList;

  // Update total coupon count with the actual number of coupons
  couponList.totalCouponCount = couponStats.totalCouponCount;
  couponList.usedCouponCount = couponStats.usedCouponCount;
  couponList.unusedCouponCount = couponStats.unusedCouponCount;

  const learnerIds = coupons
    .map((coupon) => (coupon as CampaignCouponWithId).learnerId)
    .filter((id): id is string => Boolean(id));

  const learners = await getLearnersByIds(learnerIds);
  const learnerMap = new Map<string, LearnerResponse>();

  // Add learners to map using both id and uid as keys
  learners.forEach((learner) => {
    if (learner.id) {
      learnerMap.set(learner.id, learner);
    }
    if (learner.uid && learner.uid !== learner.id) {
      learnerMap.set(learner.uid, learner);
    }
  });

  couponList.coupons = coupons
    .map((coupon) => mapCouponToLearnerWithMap(coupon, learnerMap))
    .sort(compareCouponsByCode);
  return couponList;
}

export function compareCouponsByCode(
  a: Pick<CouponListItem, "couponCode">,
  b: Pick<CouponListItem, "couponCode">,
): number {
  return a.couponCode.localeCompare(b.couponCode, undefined, {
    numeric: true,
  });
}

async function getCouponInfo(
  collectionName: "packages" | "campaigns",
  docId: string,
): Promise<{
  code: string;
  name: string;
  totalCouponCount: number;
} | null> {
  if (collectionName === "packages") {
    const packageData = await packageService.getByIdOrCode(docId);
    if (!packageData) return null;
    return {
      code: packageData.code,
      name: packageData.name,
      totalCouponCount: packageData.couponCount,
    };
  } else if (collectionName === "campaigns") {
    const campaign = await campaignService.getByIdOrCode(docId);
    if (!campaign) return null;
    return {
      code: campaign.campaignCode,
      name: campaign.name,
      totalCouponCount: campaign.couponCount,
    };
  }
  return null;
}

async function getCouponStats(
  coupons: (CouponWithId | CampaignCouponWithId)[],
) {
  const totalCoupons = coupons.length;
  const confirmedCoupons = coupons.filter((coupon) => {
    if ("isConfirmed" in coupon) {
      return coupon.isConfirmed || coupon.learnerId;
    }
    return coupon.learnerId !== null;
  }).length;
  const unconfirmedCoupons = totalCoupons - confirmedCoupons;

  return {
    totalCouponCount: totalCoupons,
    usedCouponCount: confirmedCoupons,
    unusedCouponCount: unconfirmedCoupons,
  };
}

function mapCouponToLearnerWithMap(
  coupon: CouponWithId | CampaignCouponWithId,
  learnerMap: Map<string, LearnerResponse>,
): CouponListItem {
  const { id, couponCode, password, isConfirmed, learnerId } =
    coupon as CampaignCouponWithId;

  // Try to find learner by learnerId (could be either id or uid)
  let learner: LearnerResponse | null = null;
  if (learnerId) {
    learner = learnerMap.get(learnerId) || null;
  }

  const displayName = learner?.displayName || "";

  return {
    id,
    couponCode,
    password,
    learnerId,
    isConfirmed: (isConfirmed ?? learnerId) ? true : false,
    displayName,
  };
}

async function getCoupons(
  collectionName: "packages" | "campaigns",
  docId: string,
): Promise<CouponWithId[] | undefined> {
  if (collectionName === "packages") {
    return await packageCouponService.list(docId);
  } else if (collectionName === "campaigns") {
    return await campaignCouponService.list(docId);
  }
}
